<template>
  <node-view-wrapper class="rich-text-editor-examination-table">
    <div class="table-wrapper" :style="hoverStyles" @click="handleClick">
      <!-- 検査情報の表 -->
      <table class="information-table">
        <tr>
          <th>検査日</th>
          <td data-test="date">{{ examinationInformation.date }}</td>
        </tr>
        <tr>
          <th v-html="$sanitize(escapeTextExceptHighLight('検査名', true))" />
          <td
            data-test="treatmentName"
            v-html="
              $sanitize(
                escapeTextExceptHighLight(examinationInformation.treatmentName)
              )
            "
          />
        </tr>
        <tr>
          <th v-html="$sanitize(escapeTextExceptHighLight('表示種別', true))" />
          <td
            data-test="speciesName"
            v-html="
              $sanitize(
                escapeTextExceptHighLight(examinationInformation.speciesName)
              )
            "
          />
        </tr>
      </table>

      <!-- 検査結果の表 -->
      <table class="result-table">
        <tr>
          <th
            class="topic-class"
            v-html="$sanitize(escapeTextExceptHighLight('項目区分名', true))"
          />
          <th
            class="topic"
            v-html="$sanitize(escapeTextExceptHighLight('項目名', true))"
          />
          <th
            class="result"
            v-html="$sanitize(escapeTextExceptHighLight('結果', true))"
          />
          <th
            class="unit"
            v-html="$sanitize(escapeTextExceptHighLight('単位', true))"
          />
          <th class="reference">基準値</th>
          <th
            class="comment"
            v-html="$sanitize(escapeTextExceptHighLight('コメント', true))"
          />
        </tr>
        <template
          v-for="results in makeFilteredResultGroupData(
            Object.values(resultsGroupedByTopicClassId)
          )"
        >
          <tr
            v-for="(result, n) in results"
            :key="results[0].examinationTopicClassId + '_' + n"
          >
            <td
              v-if="n === 0"
              class="topic-class"
              data-test="topicClassName"
              v-html="
                $sanitize(escapeTextExceptHighLight(result.topicClassName))
              "
              :rowspan="results.length"
            />
            <td
              class="topic"
              data-test="topicName"
              v-html="$sanitize(escapeTextExceptHighLight(result.topicName))"
            />
            <td
              class="result"
              data-test="result"
              v-html="$sanitize(escapeTextExceptHighLight(result.result))"
            />
            <td
              class="unit"
              data-test="unit"
              v-html="$sanitize(escapeTextExceptHighLight(result.unit))"
            />
            <td class="reference" data-test="reference">
              {{ result.reference }}
            </td>
            <td
              class="comment"
              data-test="comment"
              v-html="$sanitize(escapeTextExceptHighLight(result.comment))"
            />
          </tr>
        </template>
      </table>
    </div>
  </node-view-wrapper>
</template>

<script>
import moment from 'moment'
import { NodeViewWrapper, nodeViewProps } from '@tiptap/vue-2'
import { escapeHtml } from '@/utils/escape'
import { groupBy } from 'lodash'
import { highLightText } from '@/utils/high_light'
import { makeFilteredResultGroupData } from '@/utils/examination_utils'
import { mapGetters } from 'vuex'
import { unescapeHighLightHtml } from '@/utils/unescape'

export default {
  name: 'RichTextEditorExaminationTable',

  components: {
    NodeViewWrapper
  },

  props: nodeViewProps,

  computed: {
    ...mapGetters({
      getExaminationResultsByPatientIdIncludeDel:
        'examinationResults/getDataByPatientIdIncludeDel',
      newExaminationResultGroups:
        'examinationResults/getNewExaminationResultGroups',
      getTreatment: 'treatments/getDataById',
      getFormat: 'examinationFormats/getDataById',
      getTopicClass: 'examinationTopicClasses/getDataById',
      getTopic: 'examinationTopics/getDataById',
      getExaminationSpecies: 'examinationSpecies/getDataById',
      getSpecies: 'species/getDataById'
    }),
    displayExaminationResults() {
      return this.examinationResults.map(result => {
        const treatment = this.getTreatment(result.treatmentId)
        const format = this.getFormat(result.examinationFormatId)
        const topicClass = this.getTopicClass(result.examinationTopicClassId)
        const topic = this.getTopic(result.examinationTopicId)
        const examinationSpecies = this.getExaminationSpecies(
          result.examinationSpeciesId
        )
        const speciesName = examinationSpecies
          ? this.getSpecies(examinationSpecies.speciesId).name || ''
          : ''
        const reference = examinationSpecies?.reference || ''
        const numFlg = topic.examinationType === '数値' && reference !== ''
        const min = numFlg ? reference.split(',')[0] : ''
        const max = numFlg ? reference.split(',')[1] : ''
        return {
          ...result,
          treatmentName: treatment.name,
          topicClassName: topicClass.name,
          topicName: topic.name,
          speciesName,
          unit: topic.unit || '',
          resultUnit: topic.unit
            ? result.result + '(' + topic.unit + ')'
            : result.result,
          reference: min === '' && max === '' ? reference : `${min} – ${max}`,
          date: moment(result.date, 'YYYYMMDD').format('YYYY年MM月DD日（dd）'),
          treatment,
          format
        }
      })
    },
    examinationInformation() {
      const { date = '', treatmentName = '', speciesName = '' } =
        this.displayExaminationResults[0] ?? {}
      return { date, treatmentName, speciesName }
    },
    examinationResults() {
      let results = []
      if (
        !this.extension.options.richTextEditorShowFlg &&
        Object.keys(this.newExaminationResultGroups).length > 0
      ) {
        //カルテ登録・編集画面での表示
        results = this.newExaminationResultGroups[
          this.node.attrs.examinationResultGroupId
        ]
          ? this.newExaminationResultGroups[
              this.node.attrs.examinationResultGroupId
            ]
          : []
      } else if (
        this.extension.options.richTextEditorShowFlg &&
        this.extension.options.patientId > 0 &&
        this.extension.options.medicalContentId > 0
      ) {
        //カルテの履歴部分での表示
        results = this.getExaminationResultsByPatientIdIncludeDel(
          this.extension.options.patientId
        ).filter(
          v =>
            v.medicalContentId === this.extension.options.medicalContentId &&
            v.examinationResultGroupId ===
              this.node.attrs.examinationResultGroupId
        )
      }
      return results
    },
    hoverStyles() {
      return this.extension.options.richTextEditorShowFlg
        ? { '--cursor': 'auto', '--opacity': '1' }
        : { '--cursor': 'pointer', '--opacity': '0.8' }
    },
    resultsGroupedByTopicClassId() {
      return groupBy(this.displayExaminationResults, 'examinationTopicClassId')
    }
  },

  methods: {
    makeFilteredResultGroupData(data) {
      return makeFilteredResultGroupData(data)
    },
    escapeTextExceptHighLight(text, headerNameFlg = false) {
      if (
        this.extension.options.richTextEditorShowFlg &&
        this.extension.options.searchRegExp &&
        text.length > 0
      ) {
        const highLightedText = highLightText(
          text,
          this.extension.options.searchRegExp
        )
        if (headerNameFlg) {
          return highLightedText
        } else {
          const escapedContent = escapeHtml(highLightedText)
          return unescapeHighLightHtml(escapedContent)
        }
      } else {
        return escapeHtml(text)
      }
    },
    handleClick() {
      if (!this.extension.options.richTextEditorShowFlg) {
        this.$parent.$emit('click-examination-result-table', {
          examinationResultGroupId: this.node.attrs.examinationResultGroupId,
          treatment: this.displayExaminationResults[0].treatment,
          format: this.displayExaminationResults[0].format,
          examinationTableCursorPosition: this.getPos()
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$result-table-cell-widths: (
  topic-class: 15%,
  topic: 15%,
  result: 11.25%,
  unit: 7.5%,
  reference: 11.25%,
  comment: 40%
);
@mixin cell {
  box-sizing: border-box;
  position: relative;
  vertical-align: top;
  padding: 3px 5px;
  min-width: 1em;
  height: 24px;
  line-height: 24px;
  font-size: 13px;
  border: 1px solid #{$light-grey};
  > * {
    margin-bottom: 0;
  }
}
@mixin table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  margin: 0 auto;
  overflow: hidden;
  > tr > th {
    @include cell;
    font-weight: bold;
    background-color: #{$gray_f0};
  }
  > tr > td {
    @include cell;
    white-space: pre-wrap;
  }
}
.rich-text-editor-examination-table {
  padding: 5px 0;
  > .table-wrapper {
    &:hover {
      cursor: var(--cursor);
      opacity: var(--opacity);
    }
    > .information-table {
      @include table;
      > tr > th {
        width: 30%;
      }
      > tr > td {
        width: 70%;
      }
    }
    > .result-table {
      @include table;
      @each $class, $value in $result-table-cell-widths {
        > tr > .#{$class} {
          width: $value;
        }
      }
    }
    > .information-table + .result-table {
      margin-top: -1px;
    }
  }
}
</style>
